import { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { COMMAND_PRIORITY_LOW, PASTE_COMMAND, $getRoot } from 'lexical';

// This plugin directly attaches a keydown listener to the editor DOM element
const DirectKeyPressPlugin = ({ findEntityMatches }) => {
  const [editor] = useLexicalComposerContext();
  const editorRef = useRef(null);

  // Get reference to the editor DOM element
  useEffect(() => {
    const rootElement = editor.getRootElement();
    if (rootElement) {
      editorRef.current = rootElement;
    }
  }, [editor]);

  useEffect(() => {
    if (!editorRef.current || !findEntityMatches) return;

    const handleKeyDown = async (event) => {
      if (
        event.key === ' ' ||
        event.key === ',' ||
        event.key === '.' ||
        event.key === '!' ||
        event.key === '?'
      ) {
        editor.getEditorState().read(async () => {
          const root = $getRoot();
          const textContent = root.getTextContent();
          await findEntityMatches(textContent);
        });
      }
    };

    editorRef.current.addEventListener('keydown', handleKeyDown);

    // eslint-disable-next-line consistent-return
    return () => {
      if (editorRef.current) {
        editorRef.current.removeEventListener('keydown', handleKeyDown);
      }
    };
  }, [editor, findEntityMatches]);

  // Register paste command handler
  useEffect(() => {
    if (!findEntityMatches) return undefined;

    return editor.registerCommand(
      PASTE_COMMAND,
      () => {
        // Use requestAnimationFrame to ensure the paste content is available in the editor
        requestAnimationFrame(() => {
          editor.getEditorState().read(async () => {
            const root = $getRoot();
            const textContent = root.getTextContent();
            await findEntityMatches(textContent);
          });
        });
        // Let the default paste behavior continue
        return false;
      },
      COMMAND_PRIORITY_LOW
    );
  }, [editor, findEntityMatches]);

  return null;
};

DirectKeyPressPlugin.propTypes = {
  findEntityMatches: PropTypes.func
};

export { DirectKeyPressPlugin };
